/* eslint-disable @typescript-eslint/no-restricted-imports */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AppRenderer, { ChildrenWidget } from 'lib-node-amb-renderer';
import { useDispatch } from 'react-redux';

import { DtoV1DocumentAssociationSaveBatchDocumentAssociations } from '~/__generated__/api';
import componentMapper from '~/common/componentMapper';
import { DynamicTableWidget } from '~/common/componentMapper/ChakraTable/DTable';
import { DownloadLinkWidget } from '~/common/componentMapper/DownloadLink/DownloadLink';
import FormTemplate from '~/common/componentMapper/FormTemplate/FormTemplate';
import { MultiSelectWidget } from '~/common/componentMapper/Select/MultiSelect';
import GlobalLayout from '~/common/components/GlobalLayout';
import Oops from '~/common/components/Oops';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import lang from '~/common/constants/language.json';
import { useUser } from '~/common/hooks/useAuthentication';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { HomeThunks } from '~/pages/Home/redux';

import { classifyKeys, useQuickMatchDocumentsQuery } from '../hooks/queries';
import layoutYaml from './referenceLayout.yaml?raw';

const toastErrorMessage = {
  severity: 'error',
  message: 'There was an issue loading data for Document Association(s)',
  duration: 5000,
} as const;

const { UPDATING, DEFAULT } = lang.LOADER_MESSAGE;

class MyTableRowWidget extends ChildrenWidget {}

const customComponents = {
  DynamicTable: DynamicTableWidget,
  DynamicTableRow: MyTableRowWidget,
  DownloadLink: DownloadLinkWidget,
  MultiSelect: MultiSelectWidget,
};

export default function QuickMatch() {
  const dispatch = useDispatch();
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const { data: quickMatchData, status: quickMatchStatus } =
    useQuickMatchDocumentsQuery();

  const { mutateAsync: saveDocumentAssociationAsync, status: saveStatus } =
    useMutation({
      mutationKey: ['classify', 'quickMatch', obfuscatedLoanIdentifier],
      mutationFn: async (
        formValues: Omit<
          DtoV1DocumentAssociationSaveBatchDocumentAssociations,
          'obfuscatedLoanIdentifier'
        >,
      ) => {
        return await typedApiClient.v1.documentAssociationSaveBatchDocumentAssociations(
          {
            obfuscatedLoanIdentifier,
            ...formValues,
          },
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [classifyKeys.all] });
        dispatch(HomeThunks.getNavigationDetails(false, isUnderwriter));
      },
      onError: () => {
        showSnackbar(toastErrorMessage);
      },
    });

  const submitting = saveStatus === 'loading';
  const isLoading =
    quickMatchStatus === 'loading' || submitting || !quickMatchData?.screenData;
  const isError = saveStatus === 'error' || quickMatchStatus === 'error';

  if (isError) {
    return <Oops />;
  }

  if (isLoading || !quickMatchData?.screenData) {
    return (
      <GlobalLayout
        heading="Document Association(s)"
        primaryButton={{
          name: 'Continue',
          isSubmitting: submitting,
          isDisabled: true,
        }}
        loadingMessage={submitting && !isError ? UPDATING : DEFAULT}
        isLoading={isLoading}
      />
    );
  }

  return (
    <AppRenderer
      onSubmit={saveDocumentAssociationAsync}
      data={quickMatchData.screenData}
      markup={layoutYaml}
      componentMapper={componentMapper}
      initialValues={quickMatchData.initialFormValues}
      FormTemplate={FormTemplate}
      customComponents={customComponents}
    />
  );
}
